import { DIRECTION_HANDLE_MAP, DIRECTION_MAP, Direction } from 'constants/bounding-box';
import { RootState, useAppStore } from 'modules/Editor/store';
import { selectActiveWidgetIds } from 'modules/Editor/store/widgetSelector';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CleanupWidgetBoundingBoxConfig, SetCustomWidgetOverride, SetWidgetRef } from 'widgets/Widget.types';

import { getWidgetElements, getWidgetRotatable } from '../BoundingBox.helpers';
import {
  BoundingBoxContextI,
  BoundingBoxProviderProps,
  BoundingBoxRef,
  Move,
  Resize,
  Rotate,
  WidgetBoundingBoxRef,
} from './useBoundingBox.types';

const DEFAULT_BOUNDING_BOX_REF = {
  resize: () => {},
  updateRect: () => {},
  move: () => {},
  rotate: () => {},
  _moveable: {},
};

const defaultBoundingBoxContext = {
  boundingBox: DEFAULT_BOUNDING_BOX_REF,
  widgetRefs: {},
  setWidgetRef: () => {},
  cleanupWidgetBoundingBoxConfig: () => {},
  setCustomWidgetOverride: () => {},
};

const BoundingBoxContext = createContext<BoundingBoxContextI>(defaultBoundingBoxContext);

export const BoundingBoxProvider = ({ children }: BoundingBoxProviderProps) => {
  const widgetRefs = useRef<WidgetBoundingBoxRef>({});
  const boundingBox = useRef<BoundingBoxRef>(DEFAULT_BOUNDING_BOX_REF);
  const store = useAppStore();

  const setWidgetRef: SetWidgetRef = useCallback((id, ref) => {
    if (ref) widgetRefs.current[id] = { ...widgetRefs.current[id], element: ref };
  }, []);

  const cleanupWidgetBoundingBoxConfig: CleanupWidgetBoundingBoxConfig = useCallback(
    (id) => {
      if (!widgetRefs.current || !widgetRefs.current[id]) return;

      // if the widget exists in infograph don't remove it from the widgetRefs
      // as the widget ref is still needed for selection
      const widgets = (store.getState() as RootState).infograph.widgets;
      if (widgets[id]) return;

      delete widgetRefs.current[id];
    },
    [store],
  );

  const setCustomWidgetOverride: SetCustomWidgetOverride = useCallback(
    (id, boundingBoxFunction, boundingBoxFunctionName) => {
      widgetRefs.current[id] = { ...widgetRefs.current[id], [boundingBoxFunctionName]: boundingBoxFunction };
    },
    [],
  );

  return (
    <BoundingBoxContext.Provider
      value={{
        setWidgetRef,
        cleanupWidgetBoundingBoxConfig,
        setCustomWidgetOverride,
        boundingBox: boundingBox.current,
        widgetRefs: widgetRefs.current,
      }}
    >
      {children}
    </BoundingBoxContext.Provider>
  );
};

const isInstant = true;

export const useExtendBoundingBoxWithMoveable = ({
  boundingBox,
  moveableRef,
}: {
  boundingBox: BoundingBoxRef;
  moveableRef: any;
}) => {
  const store = useAppStore();

  useEffect(() => {
    const resize = ({
      width,
      height,
      deltaWidth = 0,
      deltaHeight = 0,
      keepRatio = false,
      side = Direction.SOUTH_EAST,
    }: Resize) => {
      if (!moveableRef.current) return;

      const { renderDirections } = moveableRef.current.props;
      const handleDirection = DIRECTION_HANDLE_MAP[side];

      // Prevent resizing from handle that doesn't exist on the selected bounding box
      if (!renderDirections.includes(handleDirection)) return;

      const request: Resize = { keepRatio, direction: DIRECTION_MAP[side], deltaWidth, deltaHeight };
      if (height) request.offsetHeight = height;
      if (width) request.offsetWidth = width;

      moveableRef.current.request('resizable', request, isInstant);
    };

    const move = ({ deltaX = 0, deltaY = 0 }: Move) => {
      const request: Move = { deltaX, deltaY };

      return moveableRef.current?.request('draggable', request, isInstant);
    };

    const rotate = ({ deltaRotate = 0 }: Rotate) => {
      const request: Rotate = { deltaRotate };

      // we only need the active widget ids in here when this function is called
      // moving this outside to a selector leads to a lot of re-rendering
      const activeWidgetIds = selectActiveWidgetIds(store.getState() as RootState);
      const isRotatable = getWidgetRotatable(activeWidgetIds);
      if (!isRotatable) return;

      return moveableRef.current?.request('rotatable', request, isInstant);
    };

    const updateRect = () => moveableRef.current?.updateRect();

    // exposed to widgets
    boundingBox.resize = resize;
    boundingBox.rotate = rotate;
    boundingBox.move = move;
    boundingBox.updateRect = updateRect;
    boundingBox._moveable = moveableRef.current;
  }, [boundingBox, moveableRef, store]);
};

export const EmptyBoundingBoxProvider = ({ children }: BoundingBoxProviderProps) => {
  return <BoundingBoxContext.Provider value={defaultBoundingBoxContext}>{children}</BoundingBoxContext.Provider>;
};

export const useBoundingBox = (): BoundingBoxContextI => {
  const boundingBox = useContext(BoundingBoxContext);

  return boundingBox;
};

/**
 * This hook retrieves the widget elements associated with the provided `activeWidgetIds` from the DOM.
 * It makes use of `useLayoutEffect` to access and update the widget elements immediately after
 * the component's render phase, ensuring the elements are updated before any visual screen updates occur.
 * By doing so, it helps in synchronously reading the layout from the DOM and then synchronously rendering updates.
 * This functionality was previously located in the `boundingbox.tsx` component, but was refactored for better reusability.
 *
 * @param {Object} params - The parameters object.
 * @param {string[]} params.activeWidgetIds - An array of widget IDs for which the DOM elements need to be retrieved.
 * @param {WidgetBoundingBoxRef} params.widgetRefs - The reference object containing widget references from other components.
 *
 * @returns {Object} Returns an object containing:
 *   - widgetElements: An array of HTMLElements that represent the DOM elements associated with the provided widget IDs.
 */
export const useExtendBoundingBoxElements = ({
  activeWidgetIds,
  widgetRefs,
}: {
  activeWidgetIds: string[];
  widgetRefs: WidgetBoundingBoxRef;
}) => {
  const [widgetElements, setWidgetElements] = useState<HTMLElement[]>([]);
  useLayoutEffect(() => {
    if (!activeWidgetIds || !activeWidgetIds.length) {
      setWidgetElements([]);
      return;
    }
    const newElements = getWidgetElements(activeWidgetIds, widgetRefs);
    setWidgetElements(newElements);
  }, [activeWidgetIds, widgetRefs]);

  return { widgetElements: widgetElements };
};
